import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { CardContent, Grid } from '@mui/material';
import { HttpStatusCode, isAxiosError } from 'axios';
import { formatCurrency } from 'common/constants/currency';
import { Fragment, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { formatDate, formatFirstInstallmentDate } from 'utils/formatDate';
import { useStoreSelector } from 'v3/data/store';
import { ContractingStateEnum } from 'v3/data/store/contracting/enums';
import { showErrorDialog } from 'v3/data/store/error/actions';
import {
  setProposalFinancingDetails,
  setProposalFinancingInformations,
  setProposalProductPrice,
} from 'v3/data/store/proposal/actions';
import { IContractingDetailsParams } from 'v3/pages/Contracting/Details/Cards/types';
import * as api from 'v3/services/api';
import { calculateProductFinalPrice } from 'v3/services/utils/calculateFinalPrice';
import { BodyLarge } from 'v3/shared/typography/BodyLarge';
import { BodySmallBold, BodySmallRegular } from 'v3/shared/typography/BodySmall';
import { SubtitleLarge } from 'v3/shared/typography/SubtitleLarge';
import { TitleSmall } from 'v3/shared/typography/TitleSmall';
import { theme } from 'v3/styles/global-styles';
import {
  CardHeaderStyled,
  CardStyled,
  Content,
  DividerStyled,
  TextDivider,
  TextOpacity,
  TextRow,
  TextSeparate,
} from '../styles';

export const ProposalInformation = () => {
  const { financing, product, financingDetails } = useStoreSelector((store) => store.proposal);
  const { timeRemaining } = useStoreSelector((store) => store.contracting);
  const { contractSignDate } = useStoreSelector((store) => store.contracting.selectedContracting);
  const { firstInstallmentDate } = useStoreSelector((store) => store.proposal.financing);
  const { contractingState } = useStoreSelector((store) => store.contracting);
  const { contractingKey } = useParams<Readonly<IContractingDetailsParams>>() as IContractingDetailsParams;
  const discount = useStoreSelector((store) => store.proposal.product.discount);
  const insuranceValue = useStoreSelector((store) => store.proposal.financingDetails.insuranceValue);
  const accessoriesValue = useStoreSelector((store) => store.proposal.financingDetails.accessoriesValue);
  const finalPrice = calculateProductFinalPrice(product.price, discount);
  const productSubtotal = finalPrice + insuranceValue + accessoriesValue;

  const getProposalInformation = useCallback(async () => {
    try {
      const responseGetFinancing = await api.getFinancing(contractingKey);
      setProposalFinancingInformations(responseGetFinancing.data);

      const responseGetProduct = await api.getProduct(contractingKey);
      setProposalProductPrice(responseGetProduct.data);

      const responseGetFinancingDetails = await api.getFinancingDetails(contractingKey);
      setProposalFinancingDetails(responseGetFinancingDetails.data);
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === HttpStatusCode.NotFound) console.error(error);
      } else {
        showErrorDialog('Erro ao obter detalhes da proposta', error);
      }
    }
  }, []);

  const handleGetProposalInformation = useCallback(async () => {
    await getProposalInformation();
  }, [contractingState]);

  useEffect(() => {
    handleGetProposalInformation();
  }, [handleGetProposalInformation]);

  return (
    <CardStyled>
      <CardHeaderStyled
        title={
          <Content>
            <ArticleOutlinedIcon style={{ color: theme.palette.error.main }} />
            <SubtitleLarge>Informações da proposta</SubtitleLarge>
          </Content>
        }
      />
      <CardContent>
        <TextDivider>
          <TextRow>
            <TextOpacity>
              <BodySmallRegular>Crédito aprovado</BodySmallRegular>
            </TextOpacity>
            <TextSeparate>
              <BodySmallBold className="empty-line">
                {financing?.releasedAmount ? formatCurrency(financing.releasedAmount) : ''}
              </BodySmallBold>
              <TitleSmall style={{ color: theme.palette.secondary.light }}>
                Porcentagem mínima: {financing?.minimumEntryPercentage ? `${financing.minimumEntryPercentage}%` : '-'}
              </TitleSmall>
            </TextSeparate>
          </TextRow>

          <TextRow>
            <TextOpacity>
              <BodySmallRegular>{product.name ? product.name : '-'}</BodySmallRegular>
            </TextOpacity>
            <TextSeparate>
              <BodySmallBold className="empty-line">
                {product.name ? formatCurrency(product.price - product.discount) : ''}
              </BodySmallBold>
            </TextSeparate>
          </TextRow>
        </TextDivider>

        <DividerStyled />

        <TextDivider>
          <BodySmallBold color={theme.palette.secondary.light}>Itens adicionados</BodySmallBold>

          <TextRow>
            <TextOpacity>
              <BodySmallRegular>Seguro</BodySmallRegular>
            </TextOpacity>
            <TextSeparate>
              <BodySmallBold className="empty-line">
                {financingDetails?.insuranceValue ? financingDetails.insuranceName : ''}
              </BodySmallBold>
            </TextSeparate>
          </TextRow>

          <TextRow>
            <TextOpacity>
              <BodySmallRegular>Valor do seguro</BodySmallRegular>
            </TextOpacity>
            <TextSeparate>
              <BodySmallBold className="empty-line">
                {financingDetails?.insuranceValue ? formatCurrency(financingDetails.insuranceValue) : ''}
              </BodySmallBold>
            </TextSeparate>
          </TextRow>

          <TextRow>
            <TextOpacity>
              <BodySmallRegular>Valor dos acessórios</BodySmallRegular>
            </TextOpacity>
            <TextSeparate>
              <BodySmallBold className="empty-line">
                {financingDetails?.accessoriesValue ? formatCurrency(financingDetails.accessoriesValue) : ''}
              </BodySmallBold>
            </TextSeparate>
          </TextRow>

          <TextRow>
            <TextOpacity>
              <BodySmallRegular>Subtotal dos produtos</BodySmallRegular>
            </TextOpacity>
            <TextSeparate>
              <BodySmallBold className="empty-line">
                {productSubtotal ? formatCurrency(productSubtotal) : ''}
              </BodySmallBold>
            </TextSeparate>
          </TextRow>
        </TextDivider>

        <DividerStyled />

        <TextDivider>
          <TextRow>
            <TextOpacity>
              <BodySmallRegular>Valor da entrada</BodySmallRegular>
            </TextOpacity>
            <TextSeparate>
              <BodySmallBold className="empty-line">
                {financing.inputValue ? `${formatCurrency(financing.inputValue)}` : ''}
              </BodySmallBold>
            </TextSeparate>
          </TextRow>

          <TextRow>
            <TextOpacity>
              <BodySmallRegular>Parcelas</BodySmallRegular>
            </TextOpacity>
            <TextSeparate>
              <BodySmallBold className="empty-line">
                {financing?.numberOfInstallments && financing?.installmentValue
                  ? `${financing.numberOfInstallments}x de ${formatCurrency(financing.installmentValue)}`
                  : ''}
              </BodySmallBold>
            </TextSeparate>
          </TextRow>

          <TextRow>
            <TextOpacity>
              <BodySmallRegular>Total a pagar (c/juros)</BodySmallRegular>
            </TextOpacity>
            <TextSeparate>
              <BodySmallBold className="empty-line">
                {financing?.totalFinancingAmount ? `${formatCurrency(financing.totalFinancingAmount)} ` : ''}
              </BodySmallBold>
            </TextSeparate>
          </TextRow>

          <TextRow>
            <TextOpacity>
              <BodySmallRegular>Valor financiado (Valor repasse)</BodySmallRegular>
            </TextOpacity>
            <TextSeparate>
              <BodyLarge className="empty-line">
                {financing?.requestedAmount ? formatCurrency(financing.requestedAmount) : ''}
              </BodyLarge>
            </TextSeparate>
          </TextRow>

          <BodySmallBold color={theme.palette.secondary.light}>Detalhes</BodySmallBold>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 2 }}>
              <TextOpacity>
                <BodySmallRegular>Vencimento da 1º parcela</BodySmallRegular>
              </TextOpacity>
              <BodySmallBold className="empty-line">
                {firstInstallmentDate ? formatFirstInstallmentDate(firstInstallmentDate) : ''}
              </BodySmallBold>
            </Grid>

            <Grid size={{ xs: 12, sm: 2 }}>
              {contractingState !== ContractingStateEnum.ContractingFinished ? (
                <Fragment>
                  <TextOpacity>
                    <BodySmallRegular>Vencimento da proposta</BodySmallRegular>
                  </TextOpacity>
                  <BodySmallBold className="empty-line">{timeRemaining}</BodySmallBold>
                </Fragment>
              ) : (
                <Fragment>
                  <TextOpacity>
                    <BodySmallRegular>Data do financiamento</BodySmallRegular>
                  </TextOpacity>
                  <BodySmallBold className="empty-line">
                    {contractSignDate ? formatDate(contractSignDate).date : ''}
                  </BodySmallBold>
                </Fragment>
              )}
            </Grid>
          </Grid>
        </TextDivider>
      </CardContent>
    </CardStyled>
  );
};
